/**
 * WorkQ cell inventory (display vs editable, popups):
 * - date/datetime: mostly display (DateDisplay); next_fu_date editable via lazy DatePicker on activate.
 * - add_notes / add_tasks / add_alerts: buttons; lists + modals routed through this shell (no per-row portals).
 * - mrq_notes: hover preview + click-to-edit modal via shell.
 * - gdq_status: select + "Other" modal via shell; hover hint via WorkQHoverHint.
 * - status (editable payer): StatusCellPayerType / StatusCellPayerStatus lazy-loaded.
 * - text + MultipleRowsCell tooltips: WorkQHoverHint when provider present; else legacy modal wrappers.
 */
import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
    Suspense,
    lazy,
} from "react";
import ReactDOM from "react-dom";
import { useWorkQEditSession } from "../../../lib/hooks/useWorkQEditSession";

import OutsideClickComponent from "../../fragments/OutsideClickComponent";
import Backdrop from "../../common/modal/Backdrop";
import Modal from "../../common/Modal";
import ResolveTasks from "../../patient/details/notes/ResolveTasks";
import ResolveAndCreate from "../../patient/details/notes/ResolveAndCreate";
import ResolveAlerts from "../../patient/details/alerts/ResolveAlerts";

import noteClasses from "../cells/NoteCell.module.css";
import modalClasses from "../../common/modal/BodySimpleModal.module.css";

const NotesVertical = lazy(() => import("../../patient/details/notes/NotesVertical"));
const AddNotes = lazy(() => import("../../patient/details/notes/AddNotes"));
const AlertsVertical = lazy(() => import("../../patient/details/alerts/AlerstVertical"));
const AddAlerts = lazy(() => import("../../patient/details/alerts/AddAlerts"));
const WorkQGdqOtherModal = lazy(() => import("../modals/WorkQGdqOtherModal"));
const WorkQMrqNotesModal = lazy(() => import("../modals/WorkQMrqNotesModal"));
const WorkQMrqCoverLetterModal = lazy(() => import("../modals/WorkQMrqCoverLetterModal"));

const WorkQShellContext = createContext(null);

function cellSuspenseFallback() {
    return (
        <span className="loader" style={{ display: "inline-block", minWidth: 24, minHeight: 24 }} />
    );
}

export function WorkQShellProvider({ children }) {
    const [session, setSession] = useState(null);
    const sessionRef = useRef(null);
    const hoverCloseTimerRef = useRef(null);
    const isEditModal = session?.layer === "modal";

    sessionRef.current = session;

    useWorkQEditSession(isEditModal);

    const cancelScheduledPopoverClose = useCallback(() => {
        if (hoverCloseTimerRef.current) {
            clearTimeout(hoverCloseTimerRef.current);
            hoverCloseTimerRef.current = null;
        }
    }, []);

    const schedulePopoverClose = useCallback(() => {
        cancelScheduledPopoverClose();
        hoverCloseTimerRef.current = setTimeout(() => {
            setSession((s) => (s && s.layer === "popover" ? null : s));
            hoverCloseTimerRef.current = null;
        }, 200);
    }, [cancelScheduledPopoverClose]);

    const close = useCallback(() => {
        cancelScheduledPopoverClose();
        setSession(null);
    }, [cancelScheduledPopoverClose]);

    const openPopover = useCallback(
        (spec) => {
            // Do not let hover/click popovers steal an open create/edit modal session.
            if (sessionRef.current?.layer === "modal") return;
            cancelScheduledPopoverClose();
            setSession({ ...spec, layer: "popover" });
        },
        [cancelScheduledPopoverClose],
    );

    const openModal = useCallback(
        (spec) => {
            cancelScheduledPopoverClose();
            setSession({ ...spec, layer: "modal" });
        },
        [cancelScheduledPopoverClose],
    );

    const removeOpenListItem = useCallback((id) => {
        setSession((current) => {
            if (!current || !Array.isArray(current.notes)) {
                return current
            }
            return {
                ...current,
                notes: current.notes.filter((item) => item?.id != id),
            }
        })
    }, [])

    const [tooltip, setTooltip] = useState(null);

    const showTooltip = useCallback((content, anchorEl, options = {}) => {
        if (content == null || content === false || !anchorEl) {
            setTooltip(null);
            return;
        }
        const r = anchorEl.getBoundingClientRect();
        setTooltip({
            content,
            top: r.top,
            left: r.left + r.width / 2,
            widthAuto: !!options.widthAuto,
            fixedWidth: options.fixedWidth,
            bottomDropDirection: !!options.bottomDropDirection,
        });
    }, []);

    const hideTooltip = useCallback(() => setTooltip(null), []);

    const value = useMemo(
        () => ({
            openPopover,
            openModal,
            close,
            removeOpenListItem,
            schedulePopoverClose,
            cancelScheduledPopoverClose,
            showTooltip,
            hideTooltip,
        }),
        [
            openPopover,
            openModal,
            close,
            removeOpenListItem,
            schedulePopoverClose,
            cancelScheduledPopoverClose,
            showTooltip,
            hideTooltip,
        ],
    );

    return (
        <WorkQShellContext.Provider value={value}>
            {children}
            <WorkQShellHost session={session} close={close} openModal={openModal} />
            {tooltip &&
                ReactDOM.createPortal(
                    <div
                        className={`${modalClasses.fixed_message} ${tooltip.widthAuto ? modalClasses.width_auto : ""} ${tooltip.fixedWidth ? modalClasses.fixed_width : ""} ${tooltip.bottomDropDirection ? modalClasses.bottom_drop_direction : ""}`}
                        style={{
                            top: tooltip.top,
                            left: tooltip.left,
                            width: tooltip.fixedWidth ? tooltip.fixedWidth : "",
                        }}
                        role="tooltip"
                    >
                        {tooltip.content}
                    </div>,
                    document.getElementById("overlay-root"),
                )}
        </WorkQShellContext.Provider>
    );
}

export function useWorkQShell() {
    const ctx = useContext(WorkQShellContext);
    return ctx;
}

function popoverShellStyle(rect) {
    return {
        position: "fixed",
        top: rect.bottom + 4,
        left: rect.left,
        zIndex: 10050,
    };
}

function WorkQNotesListPopoverSession({ session, close, openModal }) {
    const [showResolveTask, setShowResolveTask] = useState(false);
    const [itemToResolve, setItemToResolve] = useState(null);
    const style = popoverShellStyle(session.rect);
    const hoverBridge = session.dismissMode === "hover-bridge";
    const wrapMouse = hoverBridge
        ? {
              onMouseEnter: session.onPopoverMouseEnter,
              onMouseLeave: session.onPopoverMouseLeave,
          }
        : {};

    const openAdd = () => {
        close();
        openModal({
            kind: "modal_add_note",
            caseData: session.caseData,
            workq: session.workq,
        });
    };

    return (
        <>
            <OutsideClickComponent task={close} ignorePortalRootClicks>
                <div {...wrapMouse} style={style} className={noteClasses.notes_container}>
                    <Suspense fallback={cellSuspenseFallback()}>
                        <NotesVertical
                            openModal={openAdd}
                            inWorkQ={true}
                            list={session.notes}
                            hasResolve={true}
                            workqId={session.workq?.id}
                            openResolveTask={() => setShowResolveTask((p) => !p)}
                            setitemToResolve={setItemToResolve}
                            listTitle={"Latest 5"}
                            caseData={session.caseData}
                        />
                    </Suspense>
                </div>
            </OutsideClickComponent>
            <ResolveTasks
                showResolveTask={showResolveTask}
                openResolveTask={() => setShowResolveTask((p) => !p)}
                itemData={itemToResolve}
                inWorkQ={true}
            />
        </>
    );
}

function WorkQTasksListPopoverSession({ session, close, openModal }) {
    const [showResolveTask, setShowResolveTask] = useState(false);
    const [itemToResolve, setItemToResolve] = useState(null);
    const style = popoverShellStyle(session.rect);
    const hoverBridge = session.dismissMode === "hover-bridge";
    const wrapMouse = hoverBridge
        ? {
              onMouseEnter: session.onPopoverMouseEnter,
              onMouseLeave: session.onPopoverMouseLeave,
          }
        : {};

    const openAdd = () => {
        close();
        openModal({
            kind: "modal_add_task",
            caseData: session.caseData,
            workq: session.workq,
        });
    };

    return (
        <>
            <OutsideClickComponent task={close} ignorePortalRootClicks>
                <div {...wrapMouse} style={style} className={noteClasses.notes_container}>
                    <Suspense fallback={cellSuspenseFallback()}>
                        <NotesVertical
                            openModal={openAdd}
                            inWorkQ={true}
                            list={session.notes}
                            hasResolve={true}
                            workqId={session.workq?.id}
                            openResolveTask={() => setShowResolveTask((p) => !p)}
                            setitemToResolve={setItemToResolve}
                            listTitle={"Tasks: first 5 due items"}
                            caseData={session.caseData}
                        />
                    </Suspense>
                </div>
            </OutsideClickComponent>
            <ResolveAndCreate
                showResolveTask={showResolveTask}
                setShowResolveTask={() => setShowResolveTask((p) => !p)}
                itemData={itemToResolve}
                inWorkQ={true}
                workqId={session.workq?.id}
                caseData={session.caseData}
            />
        </>
    );
}

function WorkQAlertsListPopoverSession({ session, close, openModal }) {
    const [showResolveTask, setShowResolveTask] = useState(false);
    const [itemToResolve, setItemToResolve] = useState(null);
    const style = popoverShellStyle(session.rect);
    const hoverBridge = session.dismissMode === "hover-bridge";
    const wrapMouse = hoverBridge
        ? {
              onMouseEnter: session.onPopoverMouseEnter,
              onMouseLeave: session.onPopoverMouseLeave,
          }
        : {};

    const openAdd = () => {
        close();
        openModal({
            kind: "modal_add_alert",
            caseData: session.caseData,
            workq: session.workq,
        });
    };

    return (
        <>
            <OutsideClickComponent task={close} ignorePortalRootClicks>
                <div
                    {...wrapMouse}
                    style={style}
                    className={`${noteClasses.notes_container} ${noteClasses.notes_container_alerts}`}
                >
                    <Suspense fallback={cellSuspenseFallback()}>
                        <AlertsVertical
                            openModal={openAdd}
                            inWorkQ={true}
                            list={session.notes}
                            hasResolve={true}
                            workqId={session.workq?.id}
                            openResolveTask={() => setShowResolveTask((p) => !p)}
                            setitemToResolve={setItemToResolve}
                        />
                    </Suspense>
                </div>
            </OutsideClickComponent>
            <ResolveAlerts
                showResolveTask={showResolveTask}
                openResolveTask={() => setShowResolveTask((p) => !p)}
                itemData={itemToResolve}
                inWorkQ={true}
            />
        </>
    );
}

function WorkQShellHost({ session, close, openModal }) {
    if (!session) return null;

    if (session.layer === "modal") {
        if (session.kind === "modal_add_note") {
            return (
                <>
                    {ReactDOM.createPortal(<Backdrop hide={close} noBlur />, document.getElementById("backdrop-root"))}
                    {ReactDOM.createPortal(
                        <Modal>
                            <Suspense fallback={cellSuspenseFallback()}>
                                <AddNotes
                                    directData={session.caseData}
                                    closeModal={close}
                                    inWorkQ={true}
                                    workqId={session.workq?.id}
                                    forced={"note"}
                                    modalTitle={"Add a note"}
                                    folder={"notes"}
                                    selectedNoteTypeId={session.workq?.note_type_id}
                                    workq={session.workq}
                                />
                            </Suspense>
                        </Modal>,
                        document.getElementById("overlay-root"),
                    )}
                </>
            );
        }
        if (session.kind === "modal_add_task") {
            return (
                <>
                    {ReactDOM.createPortal(<Backdrop hide={close} noBlur />, document.getElementById("backdrop-root"))}
                    {ReactDOM.createPortal(
                        <Modal>
                            <Suspense fallback={cellSuspenseFallback()}>
                                <AddNotes
                                    directData={session.caseData}
                                    closeModal={close}
                                    inWorkQ={true}
                                    workqId={session.workq?.id}
                                    forced={"task"}
                                    modalTitle={"Add a task"}
                                    folder={"task_notes"}
                                    selectedNoteTypeId={session.workq?.note_type_id}
                                    workq={session.workq}
                                />
                            </Suspense>
                        </Modal>,
                        document.getElementById("overlay-root"),
                    )}
                </>
            );
        }
        if (session.kind === "modal_add_alert") {
            return (
                <>
                    {ReactDOM.createPortal(<Backdrop hide={close} />, document.getElementById("backdrop-root"))}
                    {ReactDOM.createPortal(
                        <Modal>
                            <Suspense fallback={cellSuspenseFallback()}>
                                <AddAlerts closeModal={close} directData={session.caseData} inWorkQ={true} workqId={session.workq?.id} />
                            </Suspense>
                        </Modal>,
                        document.getElementById("overlay-root"),
                    )}
                </>
            );
        }
        if (session.kind === "gdq_other") {
            return (
                <Suspense fallback={null}>
                    <WorkQGdqOtherModal onClose={close} caseData={session.caseData} />
                </Suspense>
            );
        }
        if (session.kind === "modal_mrq_notes") {
            return (
                <Suspense fallback={null}>
                    <WorkQMrqNotesModal
                        onClose={close}
                        caseData={session.caseData}
                        workq={session.workq}
                        initialNote={session.initialNote}
                    />
                </Suspense>
            );
        }
        if (session.kind === "modal_mrq_cover_letter") {
            return (
                <Suspense fallback={null}>
                    <WorkQMrqCoverLetterModal
                        onClose={close}
                        caseData={session.caseData}
                        pdfBlobUrl={session.pdfBlobUrl}
                    />
                </Suspense>
            );
        }
        return null;
    }

    if (session.layer === "popover") {
        if (!session.rect) return null;
        if (session.kind === "notes_list") {
            return <WorkQNotesListPopoverSession session={session} close={close} openModal={openModal} />;
        }
        if (session.kind === "tasks_list") {
            return <WorkQTasksListPopoverSession session={session} close={close} openModal={openModal} />;
        }
        if (session.kind === "alerts_list") {
            return <WorkQAlertsListPopoverSession session={session} close={close} openModal={openModal} />;
        }
    }

    return null;
}

/**
 * Hover tooltip — single portal in WorkQShellProvider (not per cell BodySimpleModalV3).
 */
export function WorkQHoverHint({
    message,
    disabled,
    children,
    isFlex,
    widthAuto,
    fixedWidth,
    bottomDropDirection,
    className,
}) {
    const ctx = useWorkQShell();
    if (!ctx) {
        return children;
    }
    const { showTooltip, hideTooltip } = ctx;

    const wrapperClass = [className, isFlex ? modalClasses.flex_container : ""].filter(Boolean).join(" ") || undefined;

    return (
        <div
            className={wrapperClass}
            style={isFlex ? { width: "100%", height: "100%" } : undefined}
            onMouseEnter={(e) => {
                if (!disabled && message != null && message !== "") {
                    showTooltip(message, e.currentTarget, { widthAuto, fixedWidth, bottomDropDirection });
                }
            }}
            onMouseLeave={() => hideTooltip()}
        >
            {children}
        </div>
    );
}
